import React, { useState, useEffect, useCallback, useMemo } from 'react';
// import { useTranslation } from "react-i18next";
import debounce from "lodash.debounce";
import { Row, Col } from 'react-bootstrap';
import { motion } from "framer-motion";
import Table from 'react-bootstrap/Table';
import "./index.scss";

import ContentBox from "../../../../../components/ui/ContentBox";
import NoDataFound from "../../../../../components/ui/NoDataFound";
import Pagination from "../../../../../components/ui/Pagination";
import PaginationArrow from "../../../../../components/ui/PaginationArrow";
import SkeletonLoader from "../../../../../components/ui/SkeletonLoader";
import OverflowText from "../../../../../components/ui/Textoverflow";

import ListFilter from "../../../../../components/PostLogin/ScanInfo/ExposureManagement/ConcerningExposure/ListFilter";
import EndpointsFilter from "../../../../../components/PostLogin/ScanInfo/ExposureManagement/ConcerningExposure/EndpointFilter";

import { plusIcon, minusIcon, searchIcon, credentialsCloseIcon, credentialsOpenIcon } from "../../../../../utils/icons";
import { formatDate } from "../../../../../utils/util";

import { useScanInfoContext } from "../../../../ScanInfo/ScanInfoContext";

import { useSelector, useDispatch } from 'react-redux';
import { getBotnetLeaksList, getBotnetLeaksDataList } from "../../../../../store/actions/scanInfoAction";

const BotnetLeaks = () => {

    const botnetLeaksListResponse = useSelector((state) => state.scanInfo?.botnetLeaksList);
    const botnetLeakedDataResponse = useSelector((state) => state.scanInfo?.botnetLeakedData);

    const dispatch = useDispatch();

    const {
        domainId,
        scanId,
        organizationId,
        triggerListFetch,
        setTriggerListFetch,
        setContextCurrentPage
    } = useScanInfoContext();

    const [activeContent, setActiveContent] = useState("affectedEndpoints");
    const [selectedBotnetLeaks, setSelectedBotnetLeaks] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [currentPageSize, setCurrentPageSize] = useState(10);
    const [isEndpointLoading, setIsEndpointLoading] = useState(false);
    const [visiblePasswordId, setVisiblePasswordId] = useState(null);
    const [scanNotFound, setScanNotFound] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [listCurrentPage, setListCurrentPage] = useState(1);

    //list search state
    const [listSearch, setListSearch] = useState("");
    const [updatedListSearchValue, setUpdatedListSearchValue] = useState("");

    //affected endpoint search state
    const [endpointSearch, setEndpointSearch] = useState("");
    const [endpointsQuery, setEndpointsQuery] = useState("");
    const [endpointSearchFocused, setEndpointSearchFocused] = useState(false);

    //selected list filter
    const [listFilterValue, setListFilterValue] = useState({});

    //endpoint filter
    const [endpointFiltersValue, setEndpointFiltersValue] = useState({})
    
    const handlePageChange = (newPage) => {
        setListCurrentPage(newPage);
        setContextCurrentPage(newPage);
        setTriggerListFetch(true);
    };

    const hidePassword = (data) => {
        if (data.length <= 2) {
            return data; // Return as is if the length is 2 or less
        }
    
        const firstChar = data[0]; // First character
        const lastChar = data[data.length - 1]; // Last character
        const middle = '*'.repeat(data.length - 2); // Replace middle characters with '*'
    
        return `${firstChar}${middle}${lastChar}`;
    }

    const showPassword = (id) => {
        if (visiblePasswordId === id) {
            setVisiblePasswordId(null); // Hide password if clicked again
        } else {
            setVisiblePasswordId(id); // Show password for the clicked row
        }
    };

    const showContent = (data) => {  
        if(activeContent === data) {
            setActiveContent("")
        } else {
            setActiveContent(data)
            if(data === "affectedEndpoints") {
                fetchAffectedEndPoints()
            }
        }
    }

    const setCurrentPageandRecordSize = (currentPage, pageSize) => {
        setCurrentPage(currentPage);
        if(pageSize !== currentPageSize) {
            setCurrentPageSize(pageSize);
            setCurrentPage(1);
        }
    }

    const handleSearchKeyDown = (event) => {
        if (event.key === "Enter" && endpointSearchFocused) {
            onSearch()
        }
    }

    const onSearch = () => {
        setCurrentPage(1)
        setEndpointsQuery(endpointSearch)
    }

    const listFilterApply = (data) => {
        setListCurrentPage(1);
        setListFilterValue(data);
        setContextCurrentPage(1);
        setTriggerListFetch(true);
    }

    const isAnyFilterActive = Object.values(listFilterValue).some(value => Boolean(value));

    const resetFilterValue = () => {
        setListCurrentPage(1);
        setListFilterValue({});
        setContextCurrentPage(1);
        setTriggerListFetch(true);
    }

    const endPointFilterApply = (data) => {
        setEndpointFiltersValue(data)
    }

    const isAnyEndpointFilterActive = Object.values(endpointFiltersValue).some(value => Boolean(value));

    const resetEndpointFilterValue = () => {
        setCurrentPage(1)
        setEndpointFiltersValue({})
    }

    const fetchAffectedEndPoints = useCallback(()=>{
        setIsEndpointLoading(true)
        const baseUrl = `&page=${currentPage}&page_size=${currentPageSize}&domain=${selectedBotnetLeaks?.domain}&scan_id=${selectedBotnetLeaks?.scan_id}&result_id=${selectedBotnetLeaks?.id}`;
        const queryParams = endpointsQuery ? `&query=${endpointsQuery}` : "";
        const newFindings = endpointFiltersValue?.newFindings?.value && endpointFiltersValue?.newFindings?.value !== "all" ? `&new_record=${endpointFiltersValue?.newFindings?.value === "yes" ? true : false}` : "";
        const url = `${baseUrl}${queryParams}${newFindings}`
        dispatch(getBotnetLeaksDataList(url));
    },[dispatch, currentPage, currentPageSize, selectedBotnetLeaks, endpointsQuery, endpointFiltersValue])

    const requestSearch = useMemo(() => debounce((searchTerm) => {        
        setUpdatedListSearchValue(searchTerm)
    }, 500), []);

    const fetchBotnetLeaksList = useCallback(()=>{
        setIsLoading(true)
        setScanNotFound(false)
        const baseUrl = `&page=${listCurrentPage}&page_size=10`
        const params = organizationId.toLowerCase() !== "all" && domainId.toLowerCase() === "all" ? `&organization_id=${organizationId}`: `&domain=${domainId}&scan_id=${scanId}`
        const queryParams = updatedListSearchValue ? `&query=${updatedListSearchValue}` : "";
        const newFindings = listFilterValue?.newFindings?.value && listFilterValue?.newFindings?.value !== "all" ? `&new_record=${listFilterValue?.newFindings?.value === "yes" ? true : false}` : "";
        const url = `${baseUrl}${params}${queryParams}${newFindings}`
        dispatch(getBotnetLeaksList(url))
    },[dispatch, domainId, scanId, organizationId, listCurrentPage, updatedListSearchValue, listFilterValue])

    useEffect(() => {
        if (selectedBotnetLeaks) {
            setCurrentPage(1);
            fetchAffectedEndPoints()
        }
    }, [currentPage, selectedBotnetLeaks, fetchAffectedEndPoints]);

    useEffect(() => {
        if (triggerListFetch) {
            fetchBotnetLeaksList();
            setTriggerListFetch(false);
        }
    }, [triggerListFetch, currentPage, setTriggerListFetch, fetchBotnetLeaksList]);

    useEffect(() => {
        setListCurrentPage(1); // reset to first page
        setContextCurrentPage(1);
        setTriggerListFetch(true); // trigger fetch
    }, [setTriggerListFetch, setContextCurrentPage, updatedListSearchValue]);

    useEffect(()=>{
        if(botnetLeaksListResponse !== null) {
            if(botnetLeaksListResponse?.status === "failed") {
                setScanNotFound(true)
            } else {
                setScanNotFound(false)
            }
            if(botnetLeaksListResponse?.results?.length > 0) {                
                setSelectedBotnetLeaks(botnetLeaksListResponse?.results[0]);
            }
            setIsLoading(false)
        }
    },[botnetLeaksListResponse])

    useEffect(()=>{
        if(botnetLeakedDataResponse !== null) {
            setIsEndpointLoading(false)
        }
    },[botnetLeakedDataResponse])

    return (
        <>
            <div className="mt-4">
                    <Row>
                        <Col lg={3}>
                            <div className="bg-white-with-shadow subdomain-list-container h-100">
                                <div className="list-heading px-3 pt-3 d-flex justify-content-between align-items-center">
                                    <h1>Botnet Leaks List</h1>
                                    {isAnyFilterActive && (<span className="reset-filter-link" onClick={resetFilterValue}>Reset Filter</span>)}
                                    <ListFilter listFilterApply={listFilterApply} listFilterValue={listFilterValue} currentPage={"botnetLeaks"} />
                                </div>
                                <div className="search-container px-3 position-relative">
                                    <span className="position-absolute search-icon">{searchIcon()}</span>
                                    <input type="text" 
                                        placeholder="Search" 
                                        className="w-100 rounded input"
                                        value={listSearch}
                                        onChange={(e)=>{                                            
                                            setListSearch(e.target.value);
                                            requestSearch(e.target.value);
                                            setListCurrentPage(1);
                                            setContextCurrentPage(1);
                                            setTriggerListFetch(true);
                                        }}
                                    />
                                </div>
                                <div className="list-main-container">
                                    { !isLoading && scanNotFound &&
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.4 }}
                                        >
                                            <NoDataFound noDataText={botnetLeaksListResponse?.description}/>
                                        </motion.div>
                                    }
                                    {
                                        isLoading && !scanNotFound ?
                                        <SkeletonLoader type={"line"} count={5} currentPage={"botnetLeaks"} />
                                        : (
                                            botnetLeaksListResponse?.results?.length > 0 &&
                                            botnetLeaksListResponse?.results.map((data, index) => {
                                                return (
                                                    <motion.div
                                                        key={index}
                                                        initial={{ opacity: 0, y: 20 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ duration: 0.4, delay: index * 0.1 }}
                                                    >
                                                        <div className={`sub-domain-list py-2 px-3 ${selectedBotnetLeaks?.id === data.id ? "active" : ""}`} onClick={()=>{
                                                            resetEndpointFilterValue()
                                                            setSelectedBotnetLeaks(data)
                                                            }}>
                                                            <div className={`d-flex align-items-center subdomain-name gap-2 mb-2 ${!data?.new_record ? "full-width" : ""}`}>
                                                                <OverflowText text={data?.name} className={"sub-domain-heading"}/>
                                                                {data.new_record && <span className="new-subdomain">New</span>}
                                                            </div>                                                   
                                                            <p className="sub-domain-name">{data?.domain}</p>
                                                        </div>
                                                    </motion.div>
                                                )
                                            })
                                        )
                                    }
                                    { !isLoading && botnetLeaksListResponse?.results?.length > 0 &&
                                        <PaginationArrow 
                                            totalCount={botnetLeaksListResponse?.meta?.total_results}
                                            currentPage={listCurrentPage}
                                            onPageChange={handlePageChange}
                                        />
                                    }
                                    { !isLoading && botnetLeaksListResponse?.results?.length === 0 &&
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.4 }}
                                        >
                                            <NoDataFound noDataText={"No Data Found"} />
                                        </motion.div>
                                    }
                                </div>
                            </div>
                        </Col>
                        <Col lg={9}>
                            <div className="main-content bg-white-with-shadow p-2 h-100">
                                {
                                    !isLoading && scanNotFound ?
                                        <motion.div
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.4 }}
                                        >
                                            <NoDataFound noDataText={botnetLeaksListResponse?.description}/>
                                        </motion.div>
                                    :
                                    (
                                        !isLoading && botnetLeaksListResponse?.results?.length === 0 ?
                                            <motion.div
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.4 }}
                                            >
                                                <NoDataFound noDataText={"No Data Found"}/>
                                            </motion.div>
                                        :
                                        <>
                                            <div className="record-container-content p-2">
                                                <div className="tab-header-container d-flex justify-content-between align-items-center" onClick={()=>{showContent("jsSecretsSummary")}}>
                                                    <h2 className="heading">Botnet Leaks Summary</h2>
                                                    {activeContent === "affectedEndpoints" ? minusIcon() : plusIcon()}
                                                </div>
                                                {
                                                    isLoading && activeContent === "jsSecretsSummary" ?
                                                        <SkeletonLoader type={"line"} count={5} currentPage={"sensitiveExpo"} />
                                                    :
                                                    <>
                                                        {activeContent === "jsSecretsSummary" && <div className="js-secrete-summary mt-3">
                                                            <motion.div
                                                                initial={{ opacity: 0, y: 20 }}
                                                                animate={{ opacity: 1, y: 0 }}
                                                                transition={{ duration: 0.4 }}
                                                            >
                                                                <Row>
                                                                    <Col lg={6} className="mb-3">
                                                                        <ContentBox label={"Name"} value={selectedBotnetLeaks?.name}/>
                                                                    </Col>
                                                                    <Col lg={6} className="mb-3">
                                                                        <ContentBox label={"IP Address"} value={selectedBotnetLeaks?.ip_address}/>
                                                                    </Col>
                                                                    <Col lg={6} className="mb-3">
                                                                        <ContentBox label={"Date"} value={formatDate(selectedBotnetLeaks?.leak_date, "date")}/>
                                                                    </Col>
                                                                    <Col lg={6} className="mb-3">
                                                                        <ContentBox label={"Malware Location"} value={selectedBotnetLeaks?.file_location}/>
                                                                    </Col>
                                                                    <Col lg={6} className="mb-3">
                                                                        <ContentBox label={"System Name"} value={selectedBotnetLeaks?.system_name}/>
                                                                    </Col>
                                                                    <Col lg={6} className="mb-3">
                                                                        <ContentBox label={"System Domain"} value={selectedBotnetLeaks?.system_domain}/>
                                                                    </Col>
                                                                    <Col lg={6} className="mb-3">
                                                                        <ContentBox label={"System Hostname"} value={selectedBotnetLeaks?.system_hostname}/>
                                                                    </Col>
                                                                    <Col lg={6} className="mb-3">
                                                                        <ContentBox label={"System Username"} value={selectedBotnetLeaks?.system_username}/>
                                                                    </Col>
                                                                    <Col lg={6} className="mb-3">
                                                                        <ContentBox label={"System Workgroup"} value={selectedBotnetLeaks?.system_workgroup}/>
                                                                    </Col>
                                                                    <Col lg={6} className="mb-3">
                                                                        <ContentBox label={"Operating System"} value={selectedBotnetLeaks?.operating_system}/>
                                                                    </Col>
                                                                </Row>
                                                            </motion.div>
                                                        </div> }
                                                    </>
                                                }                               
                                            </div>
                                            <div className="record-container-content p-2 mt-3">
                                                <div className="tab-header-container d-flex justify-content-between align-items-center" onClick={()=>{showContent("affectedEndpoints")}}>
                                                    <h2 className="heading">Leaked Data</h2>
                                                    {activeContent === "affectedEndpoints" ? minusIcon() : plusIcon()}
                                                </div>
                                                {activeContent === "affectedEndpoints" && <div className="affected-endpoint-container">
                                                    <div className="git-leaks-main-content">
                                                        <div className="search-header-container d-flex align-items-center gap-3 my-3">
                                                            <div className="d-flex gap-3" onKeyDown={handleSearchKeyDown}>
                                                                <div className="search-container position-relative">
                                                                    <span className="position-absolute">{searchIcon()}</span>
                                                                    <input type="text" 
                                                                        placeholder="Search" 
                                                                        value={endpointSearch} 
                                                                        onChange={(e)=>{
                                                                            setEndpointSearch(e.target.value)
                                                                        }}
                                                                        onFocus={() => setEndpointSearchFocused(true)}
                                                                        onBlur={() => setEndpointSearchFocused(false)}
                                                                    />
                                                                </div>
                                                                <button className="search-btn" onClick={onSearch}>Search</button>
                                                            </div>
                                                            <EndpointsFilter endPointFilterApply={endPointFilterApply} endpointFiltersValue={endpointFiltersValue} currentPage={"botnetLeaks"} />
                                                            {isAnyEndpointFilterActive && (<span className="reset-filter-link" onClick={resetEndpointFilterValue}>Reset Filter</span>)}
                                                        </div>
                                                        <div className="js-secrets-table leaks-table">
                                                            {
                                                                isEndpointLoading || isLoading ?
                                                                <SkeletonLoader type={"line"} count={5} currentPage={"botnetLeaks"} />
                                                                :
                                                                <Table responsive="lg">
                                                                    <thead>
                                                                        <tr>
                                                                            <th>URL</th>
                                                                            <th>Username</th>
                                                                            <th>Password/Hash Password</th>
                                                                            <th>Browser</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {
                                                                            botnetLeakedDataResponse?.results.length > 0 &&
                                                                            botnetLeakedDataResponse?.results.map((data, index)=>{
                                                                                return (
                                                                                    <motion.tr
                                                                                        key={index}
                                                                                        initial={{ opacity: 0, y: 20 }}
                                                                                        animate={{ opacity: 1, y: 0 }}
                                                                                        transition={{ duration: 0.4, delay: index * 0.1 }}
                                                                                    >
                                                                                        <td className="repo-url">
                                                                                            <a href={data?.url} target="_blank" className="table-url" rel="noreferrer">{data?.url}</a>
                                                                                            {data?.new_record && <div><p className="d-inline-block new-data my-2">New</p></div>}
                                                                                        </td>
                                                                                        <td>
                                                                                            {data?.username}
                                                                                        </td>
                                                                                        <td>
                                                                                            {data?.password !== null && data?.password !== "" ? <p className="password-data">
                                                                                                {visiblePasswordId === data.id
                                                                                                    ? data?.password
                                                                                                    : hidePassword(data?.password)}
                                                                                                <span onClick={() => 
                                                                                                    showPassword(data.id)
                                                                                                    }>{visiblePasswordId === data.id ? credentialsOpenIcon() : credentialsCloseIcon()}</span>
                                                                                            </p> : "-"}
                                                                                        </td>
                                                                                        <td>
                                                                                            {data?.browser}
                                                                                        </td>
                                                                                    </motion.tr>
                                                                                )
                                                                            })
                                                                        }
                                                                    </tbody>
                                                                </Table>
                                                            }
                                                            {
                                                                !isEndpointLoading && botnetLeakedDataResponse !== "" && botnetLeakedDataResponse?.results.length === 0 && 
                                                                <motion.div
                                                                    initial={{ opacity: 0, y: 20 }}
                                                                    animate={{ opacity: 1, y: 0 }}
                                                                    transition={{ duration: 0.4 }}
                                                                >
                                                                    <NoDataFound noDataText={"No Record Found"}/>
                                                                </motion.div>
                                                            }
                                                            {botnetLeakedDataResponse !== "" && botnetLeakedDataResponse?.results.length > 0 && <Pagination
                                                                className="pagination-bar"
                                                                currentPage={currentPage}
                                                                pageSize={currentPageSize}
                                                                totalCount={botnetLeakedDataResponse?.meta?.total_results}
                                                                onPageChange={setCurrentPageandRecordSize}
                                                            />}
                                                        
                                                        </div>
                                                    </div>
                                                </div>}
                                            </div>
                                        </>
                                    )
                                }                                
                            </div>
                        </Col>
                    </Row>
                </div>
        </>
        
    )
}


export default BotnetLeaks;